import type { ReactNode } from 'react';
import { useState } from 'react';

import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { DownloadIcon } from '@/shared/ui/icons';
import { Input, Select } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { Stat } from '@/shared/ui/Stat';

import { endOfDay, localDay, startOfDay, untilAsDay } from './accessLogHelpers';
import { AnswersCard } from './AnswersCard';
import { feedbackFileUrl } from './api';
import { plural } from './retentionText';
import type { FeedbackFile, FeedbackFilters, FeedbackSummary } from './types';
import { useFeedback, useFeedbackMailboxes } from './useAdmin';

/** The window the screen opens with: the last thirty days, the API's own default. */
const DEFAULT_DAYS = 30;

function defaultSince(): string {
  const start = new Date();
  start.setDate(start.getDate() - DEFAULT_DAYS);
  return startOfDay(localDay(start)) as string;
}

/**
 * Administración → Correcciones (contract v10.28): what the people of Cubas said about
 * the system's work, counted and downloadable, for Ignira to improve the triage and the
 * reading of offers.
 *
 * Every «Correcto», «Corregir a…», «Eliminar», «Devolver a la bandeja» and every line
 * touched in a quote is already stored; this screen says how much there is for a window
 * and a mailbox, and hands out the three files the API writes. The counts come from the
 * same reads as the files, so «12 corregidos» is twelve rows in «Solo los corregidos».
 *
 * The downloads are plain links, like the access log's CSV: the cookie goes with them,
 * the browser saves the file, and the API writes the line in the access log.
 */
export function FeedbackTab() {
  const [filters, setFilters] = useState<FeedbackFilters>(() => ({ since: defaultSince() }));
  const summary = useFeedback(filters);
  const mailboxes = useFeedbackMailboxes();

  function change(patch: Partial<FeedbackFilters>) {
    setFilters((current) => ({ ...current, ...patch }));
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-ink-2 text-[14px] leading-relaxed">
        Lo que dicen quienes usan la aplicación sobre su trabajo: cada «Correcto», cada «Corregir
        a…», cada correo eliminado sin responder y cada línea corregida en un presupuesto. Es lo que
        se usa para mejorar la clasificación y la lectura de ofertas. Las descargas salen{' '}
        <strong className="text-ink">seudonimizadas</strong> (sin direcciones, nombres ni teléfonos
        de los remitentes) y cada una queda anotada en el registro de accesos.
      </p>

      <form
        className="grid items-end gap-3 sm:grid-cols-3"
        aria-label="Filtros de las correcciones"
        onSubmit={(event) => event.preventDefault()}
      >
        <Input
          label="Desde"
          type="date"
          value={localDay(new Date(filters.since))}
          onChange={(event) => change({ since: startOfDay(event.target.value) ?? defaultSince() })}
        />
        <Input
          label="Hasta"
          type="date"
          value={untilAsDay(filters.until)}
          onChange={(event) => change({ until: endOfDay(event.target.value) })}
        />
        <Select
          label="Buzón"
          value={filters.mailbox ?? ''}
          onChange={(event) => change({ mailbox: event.target.value || undefined })}
        >
          <option value="">Todos</option>
          {(mailboxes.data?.items ?? []).map((mailbox) => (
            <option key={mailbox} value={mailbox}>
              {mailbox}
            </option>
          ))}
        </Select>
      </form>

      {summary.isPending ? <Spinner variant="block" label="Contando las correcciones" /> : null}
      {summary.isError ? (
        <ErrorMessage error={summary.error}>No se pudieron contar las correcciones</ErrorMessage>
      ) : null}
      {summary.data ? (
        <>
          <TriageCard summary={summary.data} filters={filters} />
          <AnswersCard filters={filters} />
          <ExtractionCard summary={summary.data} filters={filters} />
        </>
      ) : null}
    </div>
  );
}

function TriageCard({ summary, filters }: { summary: FeedbackSummary; filters: FeedbackFilters }) {
  const [onlyCorrected, setOnlyCorrected] = useState(false);
  const counts = summary.triage;
  const left = counts.left_unanswered ?? 0;
  const sheetRows = counts.reviewed + counts.trashed_unanswered + left + counts.undone;
  return (
    <Card title="Clasificación de correos">
      <p className="text-ink-3 text-[13px] leading-snug">Correos que llegaron entre esas fechas.</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <Stat
          label="Revisados"
          value={counts.reviewed}
          hint={`de ${counts.emails} ${plural(counts.emails, 'correo', 'correos')}; ${counts.confirmed} ${plural(counts.confirmed, 'confirmado', 'confirmados')} tal cual`}
        />
        <Stat
          label="Corregidos"
          value={counts.corrected}
          tone={counts.corrected > 0 ? 'warn' : 'neutral'}
          hint={`${counts.corrected_destination} de destino, ${counts.corrected_category} de tipo; ${counts.with_note} con nota`}
        />
        <Stat
          label="Eliminados sin revisar"
          value={counts.trashed_unanswered}
          hint="Con «Eliminar» aquí, sin decir si la propuesta estaba bien"
        />
        <Stat
          label="Fuera de la bandeja sin revisar"
          value={left}
          hint="Movidos o borrados en el programa de correo sin responder aquí"
        />
        <Stat
          label="Respuestas deshechas"
          value={counts.undone}
          hint="Devueltos a la bandeja después de responder"
        />
      </div>
      {counts.emails > 0 && counts.reviewed === 0 ? (
        <Callout compact tone="info" title="Nadie ha respondido todavía a estos correos.">
          Sin «Correcto» ni «Corregir a…» no hay nada que aprender: la hoja solo lleva los
          eliminados.
        </Callout>
      ) : null}
      {counts.without_body > 0 ? (
        <Callout
          compact
          tone="warn"
          title={`${counts.without_body} ${plural(counts.without_body, 'revisado ya no tiene', 'revisados ya no tienen')} el texto del correo.`}
        >
          {summary.body_retention_days === null
            ? 'Se borró antes de que la aplicación guardara los correos mientras dure el servicio: esos casos salen solo con el asunto y los adjuntos.'
            : `La aplicación lo borra a los ${summary.body_retention_days} días: esos casos salen solo con el asunto y los adjuntos. Descarga antes de ese plazo.`}
        </Callout>
      ) : null}
      <Downloads>
        <DownloadLink
          file="triage.csv"
          filters={filters}
          rows={sheetRows}
          label="Hoja de revisión (CSV)"
          hint="Para leer en Excel: lo propuesto, lo que se respondió, la nota y el enlace al correo."
        />
        <DownloadLink
          file="triage.jsonl"
          filters={filters}
          only={onlyCorrected}
          rows={onlyCorrected ? counts.corrected : counts.reviewed}
          label="Casos de eval (JSONL)"
          hint="En el formato de las pruebas del clasificador: se leen y se añaden al conjunto real."
          option={
            <OnlyToggle checked={onlyCorrected} onChange={setOnlyCorrected}>
              Solo los corregidos
            </OnlyToggle>
          }
        />
      </Downloads>
    </Card>
  );
}

function ExtractionCard({
  summary,
  filters,
}: {
  summary: FeedbackSummary;
  filters: FeedbackFilters;
}) {
  const [onlyChanged, setOnlyChanged] = useState(false);
  const counts = summary.extraction;
  const withoutOriginal = counts.quotes_read - counts.with_original;
  return (
    <Card title="Lectura de ofertas">
      <p className="text-ink-3 text-[13px] leading-snug">
        Ofertas de proveedor leídas a un presupuesto entre esas fechas.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Ofertas leídas"
          value={counts.quotes_read}
          hint={
            withoutOriginal === 0
              ? `${counts.lines_read} ${plural(counts.lines_read, 'línea leída', 'líneas leídas')}`
              : `${counts.with_original} con la lectura original guardada`
          }
        />
        <Stat
          label="Corregidas"
          value={counts.changed}
          tone={counts.changed > 0 ? 'warn' : 'neutral'}
          hint="Alguien cambió una línea o la cabecera"
        />
        <Stat label="Líneas editadas" value={counts.lines_edited} />
        {/* Deleted and added lines are only known against the reading kept since v10.28:
            with none kept, a «0 / 0» would claim nothing was deleted. */}
        <Stat
          label="Borradas y añadidas"
          value={
            counts.with_original === 0 ? '—' : `${counts.lines_removed} / ${counts.lines_added}`
          }
          hint={
            withoutOriginal === 0
              ? 'Leídas de más / que faltaban'
              : 'Solo de las que tienen la lectura original'
          }
        />
      </div>
      {withoutOriginal > 0 ? (
        <Callout
          compact
          tone="info"
          title={`${withoutOriginal} ${plural(withoutOriginal, 'oferta se leyó', 'ofertas se leyeron')} antes de guardar la lectura original.`}
        >
          De esas solo se sabe qué líneas se editaron, no lo que decían antes ni cuáles se borraron.
        </Callout>
      ) : null}
      <Downloads>
        <DownloadLink
          file="extraction.jsonl"
          filters={filters}
          only={onlyChanged}
          rows={onlyChanged ? counts.changed : counts.quotes_read}
          label="Lecturas de oferta (JSONL)"
          hint="Lo que leyó el sistema al lado de cómo quedó el presupuesto."
          option={
            <OnlyToggle checked={onlyChanged} onChange={setOnlyChanged}>
              Solo las corregidas
            </OnlyToggle>
          }
        />
      </Downloads>
    </Card>
  );
}

function Downloads({ children }: { children: ReactNode }) {
  return <div className="border-line-2 flex flex-col gap-3 border-t pt-4">{children}</div>;
}

function DownloadLink({
  file,
  filters,
  only = false,
  rows,
  label,
  hint,
  option,
}: {
  file: FeedbackFile;
  filters: FeedbackFilters;
  only?: boolean;
  rows: number;
  label: string;
  hint: string;
  option?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
      <a
        href={feedbackFileUrl(file, filters, only)}
        download
        className="bg-card text-ink ring-line hover:bg-paper-2 focus-visible:outline-accent inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-md px-3.5 text-[14px] font-medium whitespace-nowrap ring-1 ring-inset transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        <DownloadIcon size={16} />
        {label}
        <span className="text-ink-3 font-normal">
          · {rows} {plural(rows, 'fila', 'filas')}
        </span>
      </a>
      <div className="flex flex-col gap-1">
        <p className="text-ink-3 text-[13px] leading-snug">{hint}</p>
        {option}
      </div>
    </div>
  );
}

function OnlyToggle({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label className="text-ink-2 flex items-center gap-2 text-[13px]">
      <input
        type="checkbox"
        className="accent-accent size-4"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {children}
    </label>
  );
}
